import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    Chip,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    TextField,
    IconButton,
    Tooltip,
    FormControlLabel,
    Switch,
    Autocomplete
} from '@mui/material';
import { 
    Sync as SyncIcon, 
    Refresh as RefreshIcon, 
    Delete as DeleteIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import healthcareServicesService from '../services/healthcareServicesService';
import { HealthcareService } from '../types';

const HealthcareServicesPage: React.FC = () => {
    
    const [services, setServices] = useState<HealthcareService[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [stats, setStats] = useState({
        total: 0,
        cur_services: 0,
        active_services: 0,
        online_services: 0
    });
    
    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [dataSource, setDataSource] = useState<'local_database' | 'recup_live_search' | null>(null);

    // Search filters
    const [filters, setFilters] = useState({
        search: '',
        code: '',
        name: '',
        is_cur: undefined as boolean | undefined,
        specialization_id: ''
    });

    // Specializations dropdown
    const [specializations, setSpecializations] = useState<Array<{id: string, code: string, name: string, description?: string}>>([]);
    const [specializationsLoading, setSpecializationsLoading] = useState(false);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        service: null as HealthcareService | null,
        loading: false
    });

    const fetchServices = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                limit: rowsPerPage,
                offset: page * rowsPerPage,
                ...filters
            };

            const response = await healthcareServicesService.getHealthcareServices(params);
            
            if (response.success) {
                setServices(response.data);
                setTotalCount(response.pagination?.total || response.data.length);
                setDataSource(response.pagination?.source || null);
            } else {
                setError(response.error || 'Errore nel recupero delle prestazioni');
                console.error('🏥 HealthcareServicesPage: API returned error:', response.error);
            }
        } catch (err: any) {
            console.error('🏥 HealthcareServicesPage: API call failed:', err);
            setError(err.response?.data?.error || err.message || 'Errore nel recupero delle prestazioni');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await healthcareServicesService.getStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (err: any) {
            console.warn('🏥 HealthcareServicesPage: Error fetching stats:', err.message);
        }
    };

    const fetchSpecializations = async () => {
        if (specializations.length > 0) return; // Cache: carica solo una volta
        
        setSpecializationsLoading(true);
        
        try {
            console.log('🏥 Fetching ALL specializations from v4 API');
            
            const response = await healthcareServicesService.getSpecializations('name', 'asc');
            
            if (response.success && response.data) {
                setSpecializations(response.data);
                console.log(`🏥 Loaded ${response.data.length} specializations from v4 API`);
            } else {
                console.error('🏥 Failed to fetch specializations:', response);
            }
            
        } catch (err: any) {
            console.error('🏥 HealthcareServicesPage: Error fetching specializations:', err.message);
        } finally {
            setSpecializationsLoading(false);
        }
    };

    const handleSyncFromRecup = async () => {
        setSyncing(true);
        setError(null);
        
        try {
            const response = await healthcareServicesService.syncFromRecup();
            
            if (response.success) {
                await Promise.all([fetchServices(), fetchStats()]);
                alert(`Sincronizzazione completata con successo!\n\n${response.message}\n\nProcessate: ${response.stats?.processed || 0}\nCreate: ${response.stats?.created || 0}\nAggiornate: ${response.stats?.updated || 0}`);
            } else {
                setError(response.error || 'Errore nella sincronizzazione');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nella sincronizzazione');
        } finally {
            setSyncing(false);
        }
    };

    const handleSyncFilteredFromRecup = async () => {
        setSyncing(true);
        setError(null);
        
        try {
            const response = await healthcareServicesService.syncFilteredFromRecup(filters);
            
            if (response.success) {
                await Promise.all([fetchServices(), fetchStats()]);
                alert(`Sincronizzazione filtrata completata!\n\n${response.message}\n\nProcessate: ${response.stats?.processed || 0}\nCreate: ${response.stats?.created || 0}\nAggiornate: ${response.stats?.updated || 0}\n\nFiltri applicati:\n${JSON.stringify(response.appliedFilters, null, 2)}`);
            } else {
                setError(response.error || 'Errore nella sincronizzazione filtrata');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nella sincronizzazione filtrata');
        } finally {
            setSyncing(false);
        }
    };

    const handleDeleteService = async () => {
        if (!deleteDialog.service) return;
        
        setDeleteDialog(prev => ({ ...prev, loading: true }));
        setError(null);
        try {
            const response = await healthcareServicesService.deleteHealthcareService(deleteDialog.service.id);
            if (response.success) {
                setDeleteDialog({ open: false, service: null, loading: false });
                await Promise.all([fetchServices(), fetchStats()]);
                alert('Prestazione eliminata dal database locale con successo!');
            } else {
                setError(response.error || 'Errore nell\'eliminazione');
                setDeleteDialog(prev => ({ ...prev, loading: false }));
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nell\'eliminazione');
            setDeleteDialog(prev => ({ ...prev, loading: false }));
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setPage(0);
        fetchServices();
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            code: '',
            name: '',
            is_cur: undefined,
            specialization_id: ''
        });
        setPage(0);
    };

    const getCurChip = (is_cur: boolean | undefined) => {
        return (
            <Chip 
                label={is_cur ? 'CUR' : 'LOCALE'} 
                color={is_cur ? 'success' : 'default'} 
                size="small" 
            />
        );
    };

    const getOnlineChip = (online: boolean | undefined) => {
        return (
            <Chip 
                label={online ? 'ONLINE' : 'OFFLINE'} 
                color={online ? 'primary' : 'default'} 
                size="small" 
            />
        );
    };

    useEffect(() => {
        const initializePage = async () => {
            await Promise.all([fetchServices(), fetchStats()]);
            // fetchSpecializations viene chiamata solo al primo click del dropdown (lazy loading)
        };
        
        initializePage();
    }, [page, rowsPerPage]);

    // Auto-search quando cambia specialization_id
    useEffect(() => {
        if (filters.specialization_id) {
            console.log(`🏥 Specialization changed to: ${filters.specialization_id}, triggering auto-search`);
            setPage(0);
            fetchServices();
        }
    }, [filters.specialization_id]);

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Gestione Prestazioni Sanitarie
            </Typography>

            {/* Statistiche */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Totale Prestazioni
                            </Typography>
                            <Typography variant="h5">
                                {stats.total}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Prestazioni CUR
                            </Typography>
                            <Typography variant="h5">
                                {stats.cur_services}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Prestazioni Online
                            </Typography>
                            <Typography variant="h5">
                                {stats.online_services}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Prestazioni Attive
                            </Typography>
                            <Typography variant="h5">
                                {stats.active_services}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filtri */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <form onSubmit={handleSearchSubmit}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Cerca per nome/codice"
                                value={filters.search}
                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                placeholder="es. TAC, Risonanza..."
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Codice"
                                value={filters.code}
                                onChange={(e) => setFilters(prev => ({ ...prev, code: e.target.value }))}
                                placeholder="es. 88.01.5"
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Nome prestazione"
                                value={filters.name}
                                onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Autocomplete
                                size="small"
                                loading={specializationsLoading}
                                options={specializations}
                                getOptionLabel={(option) => `${option.name} (${option.code})`}
                                value={specializations.find(s => s.id === filters.specialization_id) || null}
                                onChange={(event, newValue) => {
                                    setFilters(prev => ({ 
                                        ...prev, 
                                        specialization_id: newValue?.id || '' 
                                    }));
                                }}
                                onOpen={fetchSpecializations}
                                filterOptions={(options, { inputValue }) => {
                                    // Ricerca case-insensitive per nome e codice
                                    const filtered = options.filter((option) => {
                                        const searchLower = inputValue.toLowerCase();
                                        return (
                                            option.name.toLowerCase().includes(searchLower) ||
                                            option.code.toLowerCase().includes(searchLower)
                                        );
                                    });
                                    return filtered;
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText="Nessuna specializzazione trovata"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Specializzazione"
                                        placeholder="Cerca per nome o codice..."
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const { key, ...otherProps } = props;
                                    return (
                                        <Box component="li" key={key} {...otherProps}>
                                            <Box>
                                                <Typography variant="body2">
                                                    {option.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Codice: {option.code}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={filters.is_cur === true}
                                        onChange={(e) => setFilters(prev => ({ 
                                            ...prev, 
                                            is_cur: e.target.checked ? true : undefined 
                                        }))}
                                    />
                                }
                                label="Solo CUR"
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={<SearchIcon />}
                                    disabled={loading}
                                >
                                    Cerca
                                </Button>
                                <Button
                                    onClick={clearFilters}
                                    variant="outlined"
                                    disabled={loading}
                                >
                                    Pulisci
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>

            {/* Controlli */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            onClick={() => Promise.all([fetchServices(), fetchStats()])}
                            disabled={loading}
                            startIcon={<RefreshIcon />}
                        >
                            Aggiorna
                        </Button>
                        
                        <Button
                            variant="contained"
                            onClick={handleSyncFromRecup}
                            disabled={syncing}
                            startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                            color="primary"
                        >
                            {syncing ? 'Sincronizzazione...' : 'Sincronizza da RECUP'}
                        </Button>
                        
                        <Button
                            variant="contained"
                            onClick={handleSyncFilteredFromRecup}
                            disabled={syncing || (!filters.search && !filters.code && !filters.name && !filters.specialization_id)}
                            startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                            color="secondary"
                            title={(!filters.search && !filters.code && !filters.name && !filters.specialization_id) ? 'Imposta almeno un filtro per abilitare la sincronizzazione filtrata' : 'Sincronizza solo le prestazioni filtrate correnti'}
                        >
                            {syncing ? 'Sincronizzazione...' : 'Sincronizza Filtrate'}
                        </Button>
                    </Box>
                    
                    {/* Indicatore source dati */}
                    {dataSource && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Fonte dati:
                            </Typography>
                            <Chip 
                                label={dataSource === 'recup_live_search' ? 'RECUP Live' : 'Database Locale'}
                                color={dataSource === 'recup_live_search' ? 'success' : 'default'}
                                size="small"
                                variant={dataSource === 'recup_live_search' ? 'filled' : 'outlined'}
                            />
                        </Box>
                    )}
                </Box>
                
                {dataSource === 'recup_live_search' && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="success.main">
                            ✅ Stai visualizzando risultati in tempo reale da RECUP con codici CUR ufficiali
                        </Typography>
                    </Box>
                )}
                
                {dataSource === 'local_database' && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            💾 Risultati da database locale - usa i filtri di ricerca per query RECUP in tempo reale
                        </Typography>
                    </Box>
                )}
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Tabella Prestazioni */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Codice</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Prezzo</TableCell>
                                <TableCell>Età</TableCell>
                                <TableCell>Durata</TableCell>
                                <TableCell>Flags</TableCell>
                                <TableCell>Specializzazione</TableCell>
                                <TableCell>Aggiornato</TableCell>
                                <TableCell>Azioni</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={10}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : services.length === 0 ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={10}>
                                        Nessuna prestazione trovata
                                    </TableCell>
                                </TableRow>
                            ) : (
                                services.map((service) => (
                                    <TableRow key={service.id}>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {service.code}
                                                </Typography>
                                                {service.code_type && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {service.code_type}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                    {service.name}
                                                </Typography>
                                                {service.description && (
                                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                                                        {service.description.substring(0, 50)}...
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {service.type_name && (
                                                <Chip label={service.type_name} size="small" color="info" />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {service.price && `€ ${service.price}`}
                                        </TableCell>
                                        <TableCell>
                                            {service.min_age || service.max_age ? (
                                                <Typography variant="body2">
                                                    {service.min_age || 0} - {service.max_age || '∞'}
                                                </Typography>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {service.duration ? `${service.duration} min` : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {getCurChip(service.is_cur)}
                                                {getOnlineChip(service.online)}
                                                {service.blood_draw && (
                                                    <Chip label="PRELIEVO" size="small" color="warning" />
                                                )}
                                                {service.screening && (
                                                    <Chip label="SCREENING" size="small" color="secondary" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {service.clinical_specialization_name && (
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                                    {service.clinical_specialization_name}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(service.updated_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Elimina prestazione dal database locale">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setDeleteDialog({ 
                                                        open: true, 
                                                        service, 
                                                        loading: false 
                                                    })}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Righe per pagina:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                    }
                />
            </Paper>

            {/* Dialog per eliminazione prestazione */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => !deleteDialog.loading && setDeleteDialog({ open: false, service: null, loading: false })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Conferma Eliminazione
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Sei sicuro di voler eliminare la prestazione <strong>{deleteDialog.service?.name}</strong> (Codice: <strong>{deleteDialog.service?.code}</strong>) dal database locale?
                        <br /><br />
                        <strong>Attenzione:</strong> Questa operazione è irreversibile e eliminerà la prestazione solo dal database locale, non da RECUP.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialog({ open: false, service: null, loading: false })}
                        disabled={deleteDialog.loading}
                    >
                        Annulla
                    </Button>
                    <Button
                        onClick={handleDeleteService}
                        color="error"
                        variant="contained"
                        disabled={deleteDialog.loading}
                        startIcon={deleteDialog.loading ? <CircularProgress size={20} /> : <DeleteIcon />}
                    >
                        {deleteDialog.loading ? 'Eliminazione...' : 'Elimina'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default HealthcareServicesPage;